import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { recommendationsApi } from '../../services/api'
import { toast } from 'react-hot-toast'

interface Recommendation {
  id: number
  slug: string
  symbol: string
  company_name: string
  recommendation_type: 'BUY' | 'SELL' | 'HOLD'
  entry_price: number
  target_price?: number
  stop_loss?: number
  title: string
  summary?: string
  is_premium: boolean
  status: 'draft' | 'published' | 'archived'
  created_at: string
  published_at?: string
}


const ContentManagement: React.FC = () => {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published' | 'archived'>('all')
  const [selectedItems, setSelectedItems] = useState<number[]>([])

  useEffect(() => {
    fetchContent()
  }, [])

  const fetchContent = async () => {
    try {
      setLoading(true)
      const res = await recommendationsApi.getAdminRecommendations()
      setRecommendations((res as unknown as Recommendation[]) || [])
    } catch (error) {
      console.error('Error fetching content:', error)
    } finally {
      setLoading(false)
    }
  }

  const getFilteredItems = (): Recommendation[] => {
    let filtered: Recommendation[] = [...recommendations]

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item => {
        return item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
               item.symbol.toLowerCase().includes(searchTerm.toLowerCase())
      })
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status.toLowerCase() === statusFilter)
    }

    return filtered
  }

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await recommendationsApi.deleteRecommendation(id)
        toast.success('Recommendation deleted successfully')
        await fetchContent()
      } catch (error) {
        console.error('Error deleting item:', error)
        toast.error('Failed to delete item')
      }
    }
  }

  const handleStatusChange = async (id: number, newStatus: string) => {
      try {
        const statusUpper = newStatus.toUpperCase()
        if (statusUpper === 'PUBLISHED') {
          await recommendationsApi.publishRecommendation(id)
          toast.success('Recommendation published successfully')
        } else if (statusUpper === 'ARCHIVED' || statusUpper === 'DRAFT') {
          await recommendationsApi.unpublishRecommendation(id)
          toast.success('Recommendation unpublished successfully')
        }
        await fetchContent()
      } catch (error) {
        console.error('Error changing recommendation status:', error)
        toast.error('Failed to change status')
      }
  }

  const handleBulkAction = async (action: 'publish' | 'archive' | 'delete') => {
    if (selectedItems.length === 0) return

    if (action === 'delete' && !window.confirm(`Are you sure you want to delete ${selectedItems.length} items?`)) {
      return
    }

      try {
        setLoading(true)
        if (action === 'delete') {
          await Promise.all(selectedItems.map(id => recommendationsApi.deleteRecommendation(id)))
          toast.success(`Deleted ${selectedItems.length} recommendations`)
        } else if (action === 'publish') {
          await Promise.all(selectedItems.map(id => recommendationsApi.publishRecommendation(id)))
          toast.success(`Published ${selectedItems.length} recommendations`)
        } else if (action === 'archive') {
          await Promise.all(selectedItems.map(id => recommendationsApi.unpublishRecommendation(id)))
          toast.success(`Unpublished ${selectedItems.length} recommendations`)
        }
        setSelectedItems([])
        await fetchContent()
      } catch (error) {
        console.error('Error performing bulk action:', error)
        toast.error('Failed to perform bulk action')
      } finally {
        setLoading(false)
      }

    setSelectedItems([])
  }

  const filteredItems = getFilteredItems()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Content Management</h1>
          <p className="text-gray-400 mt-1">Manage recommendations</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={() => fetchContent()}>
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Link to={`/admin/content/create-recommendation`}>
            <Button>
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Recommendation
            </Button>
          </Link>
        </div>
      </div>



      {/* Filters and Search */}
      <Card>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search recommendations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 bg-dark-900 border border-dark-600 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {selectedItems.length > 0 && (
            <div className="flex space-x-2">
              <Button size="sm" onClick={() => handleBulkAction('publish')}>
                Publish ({selectedItems.length})
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction('archive')}>
                Archive
              </Button>
              <Button size="sm" variant="danger" onClick={() => handleBulkAction('delete')}>
                Delete
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Content Table */}
      <Card padding={false}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-900 border-b border-dark-700">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedItems.length === filteredItems.length && filteredItems.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedItems(filteredItems.map(item => item.id))
                      } else {
                        setSelectedItems([])
                      }
                    }}
                    className="rounded border-dark-600"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Stock & Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {filteredItems.map((item) => (
                <tr key={item.id} className="hover:bg-dark-700">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems(prev => [...prev, item.id])
                        } else {
                          setSelectedItems(prev => prev.filter(id => id !== item.id))
                        }
                      }}
                      className="rounded border-dark-600"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-primary-400 mb-1">
                        {item.symbol} - {item.company_name}
                      </div>
                      <div className="text-sm font-medium text-white">{item.title}</div>
                      {item.summary && (
                        <div className="text-sm text-gray-400 truncate max-w-md">{item.summary}</div>
                      )}
                      {item.is_premium && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-400 mt-1">
                          Premium
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.recommendation_type === 'BUY'
                        ? 'bg-green-500/10 text-green-400'
                        : item.recommendation_type === 'SELL'
                        ? 'bg-red-500/10 text-red-400'
                        : 'bg-yellow-500/10 text-yellow-400'
                    }`}>
                      {item.recommendation_type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={item.status.toLowerCase()}
                      onChange={(e) => handleStatusChange(item.id, e.target.value)}
                      className={`text-xs rounded-full px-2 py-1 font-medium border-0 ${
                        item.status.toLowerCase() === 'published'
                          ? 'bg-green-500/10 text-green-400'
                          : item.status.toLowerCase() === 'draft'
                          ? 'bg-dark-700 text-gray-400'
                          : 'bg-red-500/10 text-red-400'
                      }`}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-400">
                    {new Date(item.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <Link to={`/dashboard/recommendations/${item.slug || item.id}`}>
                        <Button size="sm" variant="ghost">
                          <EyeIcon className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Link to={`/admin/content/edit-recommendation/${item.id}`}>
                        <Button size="sm" variant="ghost">
                          <PencilIcon className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(item.id)}
                      >
                        <TrashIcon className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">No recommendations found matching your criteria</p>
            </div>
          )}
        </div>
      </Card>

      {/* Summary Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">
              {recommendations.length}
            </p>
            <p className="text-sm text-gray-400">Total recommendations</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-400">
              {recommendations.filter(r => r.status === 'published').length}
            </p>
            <p className="text-sm text-gray-400">Published</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-400">
              {recommendations.filter(r => r.status === 'draft').length}
            </p>
            <p className="text-sm text-gray-400">Drafts</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-400">
              {recommendations.filter(r => r.is_premium).length}
            </p>
            <p className="text-sm text-gray-400">Premium Content</p>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default ContentManagement
